import React from 'react';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { getSiteUrl } from '@utils/config';
import AreaHeader from '@components/Shared/AreaHeader/AreaHeader';
import StArrowRightSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/st-arrow-right.svg';
import { SubsidiariesStyles } from './Subsidiaries.styled';

const Subsidiaries = () => {
  const router = useRouter();

  return (
    <SubsidiariesStyles>
      <Head htmlAttributes={{ lang: 'en' }}>
        <title>Subsidiaries | Clayton Homes</title>
        <meta
          name="description"
          content="Since 1956, Clayton has been providing affordable, quality homes for all lifestyles. Learn more about Clayton subsidiaries."
        />
        <link rel="canonical" href={`${getSiteUrl(router.pathname)}`} />
        <meta
          property="og:image"
          content={`${getSiteUrl('/public-assets/images/clayton-logo.jpg')}`}
        />
      </Head>

      <AreaHeader
        headerText="Subsidiaries"
        bgImage="/public-assets/images/subsidiaries/header-pattern.svg"
      />

      <div className="different-names constrained-width">
        <h2>Different Names, Same Clayton</h2>
        <p className="intro-p">
          Through its affiliates and brands, Clayton manufactures, sells,
          finances, leases and insures homes for people throughout the United
          States. Regardless of the name, you can count on the value, quality
          and affordability that is Clayton. Our modular and manufactured home
          brands:
        </p>

        <div className="desktop-brands-columns">
          <div className="desktop-brands-column">
            <div className="brand-blurb">
              <Image
                src="/public-assets/images/subsidiaries/schult.png"
                alt="Schult Homes"
                width={116}
                height={71}
              />
              <div className="h5">Schult Homes</div>
              <p className="caption">
                Schult is one of the most trusted names in housing, with over 70
                years of experience in building high-quality, affordable homes.
              </p>
            </div>

            <div className="brand-blurb">
              <Image
                src="/public-assets/images/subsidiaries/karsten.png"
                alt="Karsten Homes"
                width={116}
                height={71}
              />
              <div className="h5">Karsten Homes</div>
              <p className="caption">
                Located in our western states, Karsten takes a contemporary
                approach to design, tailoring it to fit your individual style.
              </p>
            </div>

            <div className="brand-blurb">
              <Image
                src="/public-assets/images/subsidiaries/golden-west.png"
                alt="Golden West Homes"
                width={116}
                height={71}
              />
              <div className="h5">Golden West Homes</div>
              <p className="caption">
                Golden West is the most recognized name in manufactured homes on
                the West Coast and leads the way in innovation.
              </p>
            </div>

            <div className="brand-blurb">
              <Image
                src="/public-assets/images/subsidiaries/clayton-building-solutions.png"
                alt="Clayton Building Solutions"
                width={116}
                height={71}
              />
              <div className="h5">Clayton Building Solutions</div>
              <p className="caption">
                Clayton Building Solutions specializes in commercial, military,
                and multi-family modular construction projects throughout the
                United States.
              </p>
            </div>

            <div className="brand-blurb">
              <Image
                src="/public-assets/images/subsidiaries/cavalier.png"
                alt="Cavalier Homes"
                width={116}
                height={71}
              />
              <div className="h5">Cavalier Homes</div>
              <p className="caption">
                The complete start-to-finish approach to the construction of
                your home is one of the reasons there are thousands of happy
                Cavalier homeowners enjoying the strength and beauty of the
                homes built for them!
              </p>
            </div>

            <div className="brand-blurb">
              <Image
                src="/public-assets/images/subsidiaries/buccaneer.png"
                alt="Buccaneer Homes"
                width={116}
                height={71}
              />
              <div className="h5">Buccaneer Homes</div>
              <p className="caption">
                Every Buccaneer home passes a 12-point inspection before it ever
                leaves the factory, ensuring that it is weather tight, properly
                trimmed, and outfitted the way you want it.
              </p>
            </div>
          </div>
          {/* end first desktop brand column */}

          <div className="desktop-brands-column">
            <div className="brand-blurb">
              <Image
                src="/public-assets/images/subsidiaries/crest.png"
                alt="Crest Homes"
                width={116}
                height={71}
              />
              <div className="h5">Crest Homes</div>
              <p className="caption">
                Crest Homes provides off-site, system-built housing to the
                Midwest and Mid-Atlantic States.
              </p>
            </div>

            <div className="brand-blurb">
              <Image
                src="/public-assets/images/subsidiaries/marlette.png"
                alt="Marlette Homes"
                width={116}
                height={71}
              />
              <div className="h5">Marlette Homes</div>
              <p className="caption">
                Originally founded in Marlette, Michigan in 1953, Marlette has
                been building quality homes for over 60 years and continues to
                be a distinctive brand through its custom take on home
                construction.
              </p>
            </div>

            <div className="brand-blurb">
              <Image
                src="/public-assets/images/subsidiaries/sehomes.png"
                alt="SEHomes"
                width={116}
                height={71}
              />
              <div className="h5">SEhomes</div>
              <p className="caption">
                SEhomes, Inc. is one of the home building industry’s fastest
                growing and stable companies.
              </p>
            </div>

            <div className="brand-blurb">
              <Image
                src="/public-assets/images/subsidiaries/norris.png"
                alt="Norris Homes"
                width={116}
                height={71}
              />
              <div className="h5">Norris Homes</div>
              <p className="caption">
                Located in the east, Norris has long been established as a home
                builder with a great reputation of delivering superbly built
                homes.
              </p>
            </div>

            <div className="brand-blurb">
              <Image
                src="/public-assets/images/subsidiaries/giles.png"
                alt="Giles Homes"
                width={116}
                height={71}
              />
              <div className="h5">Giles Homes</div>
              <p className="caption">
                Since 1959, Giles Industries has built over 86,000 quality homes
                with the goal of building the most affordable, highest quality
                manufactured homes in the market.
              </p>
            </div>
          </div>
          {/* end second desktop brand column */}
        </div>
        {/* end desktop brand columns */}
      </div>

      <div className="full-width-bg subsidiaries-listing">
        <div className="constrained-width">
          <h2 className="subsidiaries-heading">Our Subsidiaries</h2>

          <div className="desktop-brands-columns desktop-brands-columns-subsidiaries">
            <div className="desktop-brands-column">
              <p className="intro">
                <a
                  href="https://www.vmf.com/"
                  className="gtm-subsidiaries-vmf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Vanderbilt Mortgage and Finance, Inc.{' '}
                  <StArrowRightSvg alt="Arrow Right" />
                </a>
              </p>
              <p className="intro">
                <a
                  href="https://www.21stmortgage.com/web/21stSite.nsf/index.html"
                  className="gtm-subsidiaries-twentyfirst"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  21st Mortgage Corporation{' '}
                  <StArrowRightSvg alt="Arrow Right" />
                </a>
              </p>
              <p className="intro">
                <a
                  href="https://www.claytonhomes.com/"
                  className="gtm-subsidiaries-cmh-homes"
                >
                  CMH Homes, Inc. <StArrowRightSvg alt="Arrow Right" />
                </a>
              </p>
              <p className="intro">
                <a
                  href="https://claytonhomebuildinggroup.com/"
                  className="gtm-subsidiaries-cmh-manufact-std"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  CMH Manufacturing, Inc. <StArrowRightSvg alt="Arrow Right" />
                </a>
              </p>
              <p className="intro">
                <a
                  href="https://claytonhomebuildinggroup.com/"
                  className="gtm-subsidiaries-cmh-manufact-west"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  CMH Manufacturing West, Inc.{' '}
                  <StArrowRightSvg alt="Arrow Right" />
                </a>
              </p>
              <p className="intro">
                <a
                  href="https://sehomesenergy.com/"
                  className="gtm-subsidiaries-southern-energy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Southern Energy Homes, Inc.{' '}
                  <StArrowRightSvg alt="Arrow Right" />
                </a>
              </p>
              <p className="intro">
                <a
                  href="https://cavalieralabama.com/"
                  className="gtm-subsidiaries-cavalier"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cavalier Homes, Inc. <StArrowRightSvg alt="Arrow Right" />
                </a>
              </p>
            </div>
            {/* end first desktop brand column */}
            <div className="desktop-brands-column">
              <p className="intro">
                <a
                  href="https://claytonhomebuildinggroup.com/"
                  className="gtm-subsidiaries-properties-group"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Clayton Properties Group, Inc.{' '}
                  <StArrowRightSvg alt="Arrow Right" />
                </a>
              </p>
              <p className="intro">
                <a
                  href="https://claytonhomebuildinggroup.com/"
                  className="gtm-subsidiaries-clayton-supply"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Clayton Supply, Inc. <StArrowRightSvg alt="Arrow Right" />
                </a>
              </p>
              <p className="intro">
                <a
                  href="https://homefirstagency.com/home"
                  className="gtm-subsidiaries-homefirst"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Homefirst Agency, Inc. <StArrowRightSvg alt="Arrow Right" />
                </a>
              </p>
            </div>
            {/* end second desktop brand column */}
          </div>
          {/* end desktop brand columns */}
        </div>
      </div>
    </SubsidiariesStyles>
  );
};

export default Subsidiaries;
